import { useEffect, useState } from 'react';
import { Attempt, Bank, Pack } from './api';
import { Catalogue } from './screens/Catalogue';
import { Configure } from './screens/Configure';
import { Delivery } from './screens/Delivery';
import { Review } from './screens/Review';
import { SignIn } from './screens/SignIn';
import { Profile } from './screens/Profile';
import { StudyPlan } from './screens/StudyPlan';
import { useSession } from './auth/session';
import { BrandMark } from './Brand';
import { SiteNotices } from './SiteNotices';
import { TopNav } from './TopNav';
import { InstallPrompt } from './InstallPrompt';
import { EditProfile } from './screens/EditProfile';
import { Contribute } from './screens/Contribute';
import { Leaderboards } from './screens/Leaderboards';
import { AdminUsers } from './screens/AdminUsers';
import { AdminUsage } from './screens/AdminUsage';
import { ItemAnalysis } from './screens/ItemAnalysis';
import { Admin, ADMIN_ROLES } from './screens/Admin';
import { api, Pack as PackType } from './api';
import { rememberActiveAttempt, rememberedActiveAttempt, reopenAttempt } from './screens/Resume';

type View =
  | { name: 'catalogue' }
  | { name: 'configure'; bank: Bank; pack: Pack; topicIds?: string[] }
  | { name: 'delivery'; attempt: Attempt }
  | { name: 'review'; attemptId: string }
  | { name: 'profile' }
  | { name: 'study-plan' }
  | { name: 'contribute' }
  | { name: 'leaderboards' }
  | { name: 'admin' }
  | { name: 'admin-users' }
  | { name: 'admin-usage' }
  | { name: 'edit-profile' }
  | { name: 'item-analysis' };

export function App() {
  const [view, setView] = useState<View>({ name: 'catalogue' });
  // The contribute form needs the learner's subjects to offer a choice.
  const [packs, setPacks] = useState<PackType[]>([]);
  const { learner, loading, refresh } = useSession();

  const inTest = view.name === 'delivery';
  const { signOut } = useSession();
  // Until the reload check below settles, so the catalogue does not flash up
  // under a learner who is about to be put back into their test.
  const [reopening, setReopening] = useState(() => rememberedActiveAttempt() !== null);

  // A reload mid-test lands straight back in it. The server held the attempt —
  // answers, order and clock — so there is nothing to restore but the screen.
  useEffect(() => {
    if (!learner) return;
    const id = rememberedActiveAttempt();
    if (!id) { setReopening(false); return; }
    // Forget the attempt only when the server says it is over. A failed
    // request keeps it, so the next reload can still find the way back.
    reopenAttempt(id).then((found) => {
      if (found.kind === 'open') setView({ name: 'delivery', attempt: found.attempt });
      else if (found.kind === 'finished') rememberActiveAttempt(null);
      setReopening(false);
    });
  }, [learner?.id]);

  // Held off while reopening: the first render is the catalogue, and clearing
  // then would forget the very attempt the check above is about to reopen.
  useEffect(() => {
    if (reopening) return;
    rememberActiveAttempt(view.name === 'delivery' ? view.attempt.id : null);
  }, [view, reopening]);

  useEffect(() => {
    if (!learner) { setPacks([]); return; }
    api.get<PackType[]>('/packs').then(setPacks).catch(() => setPacks([]));
  }, [learner?.id]);

  // The study plan names a bank by id; setup needs the bank and pack records,
  // which the catalogue would normally have handed it.
  async function practise(packId: string, bankVersionId: string, topicId: string) {
    const pack = packs.find((p) => p.id === packId);
    const banks = await api.get<Bank[]>(`/banks?packId=${encodeURIComponent(packId)}`).catch(() => [] as Bank[]);
    const bank = banks.find((b) => b.bankVersionId === bankVersionId) ?? banks[0];
    // Arriving from a long plan, the learner would otherwise land mid-form.
    window.scrollTo(0, 0);
    if (bank && pack) setView({ name: 'configure', bank, pack, topicIds: [topicId] });
    else setView({ name: 'catalogue' });
  }

  // THE WALL. Nothing below renders without a session, so no screen has to
  // reason about a missing learner and none can leak by forgetting to.
  // The server refuses unauthenticated calls regardless — this only decides
  // what to show, it is not the thing that protects the data.
  if (loading || (learner && reopening)) {
    return (
      <main id="main" className="shell" tabIndex={-1}>
        <p className="muted">{reopening ? 'Returning to your test…' : 'Checking your session…'}</p>
      </main>
    );
  }
  if (!learner) {
    return (
      <>
        <a className="skip-link" href="#main">Skip to main content</a>
        <header className="bar"><BrandMark /></header>
        <main id="main" className="shell" tabIndex={-1}>
          <SignIn onDone={() => setView({ name: 'catalogue' })} />
          <SiteNotices />
        </main>
        <InstallPrompt />
      </>
    );
  }

  return (
    <>
      <a className="skip-link" href="#main">Skip to main content</a>
      <TopNav
        learner={learner}
        view={view.name}
        inTest={inTest}
        isStaff={ADMIN_ROLES.includes(learner.role)}
        onNavigate={(name) => setView({ name } as View)}
        onSignOut={() => void signOut()}
      />

      <main id="main" className="shell" tabIndex={-1}>
        {view.name === 'catalogue' && (
          <Catalogue onChoose={(bank, pack) => setView({ name: 'configure', bank, pack })}
                     onResume={(attempt) => setView({ name: 'delivery', attempt })} />
        )}
        {view.name === 'configure' && (
          <Configure key={`${view.bank.bankVersionId}:${(view.topicIds ?? []).join(',')}`}
                     bank={view.bank} pack={view.pack} initialTopicIds={view.topicIds}
                     onStart={(attempt) => setView({ name: 'delivery', attempt })}
                     onBack={() => setView({ name: 'catalogue' })} />
        )}
        {view.name === 'delivery' && (
          <Delivery attempt={view.attempt}
                    onSubmitted={(attemptId) => setView({ name: 'review', attemptId })} />
        )}
        {view.name === 'review' && (
          <Review attemptId={view.attemptId}
                  onRestart={() => setView({ name: 'catalogue' })}
                  onSignIn={() => setView({ name: 'profile' })} />
        )}
        {view.name === 'profile' && (
          <Profile onBack={() => setView({ name: 'catalogue' })}
                   onDrill={() => setView({ name: 'catalogue' })}
                   onResume={(attempt) => setView({ name: 'delivery', attempt })} />
        )}
        {view.name === 'study-plan' && (
          <StudyPlan onBack={() => setView({ name: 'catalogue' })}
                     onPractice={(packId, bankVersionId, topicId) => void practise(packId, bankVersionId, topicId)} />
        )}
        {view.name === 'contribute' && (
          <Contribute packs={packs} onBack={() => setView({ name: 'catalogue' })} />
        )}
        {view.name === 'leaderboards' && (
          <Leaderboards onBack={() => setView({ name: 'catalogue' })} />
        )}
        {view.name === 'admin-users' && (
          <AdminUsers onBack={() => setView({ name: 'catalogue' })} />
        )}
        {view.name === 'admin-usage' && (
          <AdminUsage onBack={() => setView({ name: 'catalogue' })} />
        )}
        {view.name === 'item-analysis' && (
          <ItemAnalysis onBack={() => setView({ name: 'catalogue' })} />
        )}
        {view.name === 'edit-profile' && (
          <EditProfile onBack={() => setView({ name: 'catalogue' })}
                       onSignedOut={() => { void refresh(); setView({ name: 'catalogue' }); }} />
        )}
        {view.name === 'admin' && (
          <Admin onBack={() => setView({ name: 'catalogue' })} />
        )}
        {/* Withheld during delivery: a footer of policy text under a live exam
            question is a distraction, and the clock is running. */}
        {!inTest && <SiteNotices />}
      </main>
      <InstallPrompt hidden={inTest} />
    </>
  );
}
